// static/js/components/ChatInterface.jsx

/**
 * ChatInterface Component
 *
 * This React component renders an interactive chat interface for a web application.
 * It includes features such as real-time message display and management, text input,
 * and message handling with server communication for chat functionalities.
 *
 * Features:
 * - Dynamic message rendering with Markdown support for both user and assistant messages.
 * - Ability to expand and collapse long user messages.
 * - Asynchronous chat thread creation and management with live updates.
 * - Display and manage chat tool results as interactive components within the chat.
 * - Provision for handling user inputs with suggestions and intents using a custom UI.
 * - Download functionality for chat transcripts.
 * - Dialogues for managing chat limits, including thread and message restrictions.
 * - Error handling and user feedback mechanisms.
 *
 * The component uses several custom hooks and local state management techniques to handle
 * user interactions, server requests/responses, and UI state changes. It is designed to
 * handle both small-scale and intensive chat operations, ensuring a responsive and
 * user-friendly experience.
 *
 * Utilizes:
 * - React (useState, useEffect, useRef, useCallback) for component and state management.
 * - Alert and AlertDialog components for user notifications and interactions.
 * - ReactMarkdown for rendering Markdown formatted text.
 * - Custom API calls for back-end communication to fetch, send, and manage chat data.
 *
 * The ChatInterface ensures that all user interactions within the chat are smooth,
 * intuitive, and efficient, providing a comprehensive chat management system.
 */


import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Copy, Loader, Trash2, Download } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import ActivityFeedback from '@/components/ui/ActivityFeedback';
import AttachmentIndicator from '@/components/ui/AttachmentIndicator';
import ReactMarkdown from 'react-markdown';
import ToolResultDisplay from '@/components/ui/ToolResultDisplay';
import IntentIcons from '@/components/ui/IntentIcons';
import ThreadLimitDialog from '@/components/ui/ThreadLimitDialog';

// Preset Prompts ("AI guidance prompts")
import GuidancePromptsButton from './ui/GuidancePromptsButton';
import GuidancePromptsPanel from './ui/GuidancePromptsPanel';

// Tool use button
import ToolsToggleButton from './ui/ToolsToggleButton';

// Markdown sanitizer
import DOMPurify from 'dompurify';

// Configure sanitized URI validation
const SAFE_URL_PATTERN = /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

// Defines safe markdown configuration
const safeMarkdownConfig = {
  allowedElements: [
    'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'blockquote', 'code',
    'pre', 'em', 'strong', 'del', 'table',
    'tbody', 'tr', 'th', 'td', 'hr', 'br'
  ],
  disallowedElements: ['script', 'style', 'iframe', 'form', 'input', 'button'],
  allowedAttributes: {
    'a': ['href', 'title', 'target', 'rel'],
    'img': ['src', 'alt', 'title', 'width', 'height'],
    'code': ['className']
  },
  transformLinkUri: (uri) => {
    if (uri.startsWith('http://') || uri.startsWith('https://')) {
      return uri;
    }
    return '#';
  }
};

// Markdown sanitizer helper function with comprehensive security options
const sanitizeContent = (content) => {
  if (!content) return '';
  
  return DOMPurify.sanitize(content, {
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'base'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onkeydown', 
                 'onkeypress', 'onkeyup', 'formaction', 'javascript:', 'data:', 'vbscript:'],
    ADD_URI_SAFE_ATTR: ['target', 'rel'],
    ADD_ATTR: ['target', 'rel'],
    ALLOWED_URI_REGEXP: SAFE_URL_PATTERN,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    USE_PROFILES: { html: true }
  });
};

const ChatInterface = () => {

  // State declaration for tool use
  const [toolMode, setToolMode] = useState('auto'); // Options: 'auto', 'always', 'never'

  // State declarations for AI guidance prompts
  const [showGuidancePanel, setShowGuidancePanel] = useState(false);
  const [activeGuidance, setActiveGuidance] = useState(null);

  // Message truncation
  const MAX_USER_MESSAGE_TRUNCATED_SIZE_CHARACTERS = 500; // Controls how many characters to show in truncated state
  const MAX_USER_MESSAGE_LINES = 10;  // Controls how many lines to show in collapsed state

  // State management
  const [messages, setMessages] = useState([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestionOvals, setSuggestionOvals] = useState([]);
  const [isStopping, setIsStopping] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  const [threadId, setThreadId] = useState(null);
  const [shouldLoadHistory, setShouldLoadHistory] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState(new Set()); // message truncation state

  // Message deletion state  
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [isDeletingMessage, setIsDeletingMessage] = useState(false);

  // References
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const historyLoadedRef = useRef(false);

  // For the anonymous chat thread message limit exceeded dialog
  const [showMessageLimitDialog, setShowMessageLimitDialog] = useState(false);
  const [messageLimitError, setMessageLimitError] = useState(null);

  // For thread limit dialog
  const [showThreadLimitDialog, setShowThreadLimitDialog] = useState(false);
  const [threadLimitInfo, setThreadLimitInfo] = useState({
    maxThreadsLimit: 3,
    userType: 'guest'
  });
  
  //  Activity status while LLM is ruminating
  const [activityStatus, setActivityStatus] = useState('');
  const [streamingStatus, setStreamingStatus] = useState('');

  // State for managing attachments
  const [messageAttachments, setMessageAttachments] = useState({});
  const [pendingAttachments, setPendingAttachments] = useState({});

  // Handles tool use toggle button
  const handleToolModeToggle = () => {
    // Cycle through the options: auto -> always -> never -> auto
    setToolMode(prevMode => {
      switch (prevMode) {
        case 'auto': return 'always';
        case 'always': return 'never';
        case 'never': return 'auto';
        default: return 'auto';
      }
    });
  };

  // Handles AI guidance prompts
  const handleSelectGuidance = (prompt) => {
    // If clicked prompt is already active, deselect it
    if (activeGuidance && activeGuidance.name === prompt.name) {
      setActiveGuidance(null);
    } else {
      setActiveGuidance(prompt);
    }
    setShowGuidancePanel(false);

    // TODO : This will be replaced with actual implementation later
    console.log('Selected guidance prompt:', prompt.llm_prompt);
  };

  // Handles the anonymous user message length check
  const MessageLimitDialog = ({ error, onClose, onNewChat }) => {
    if (!error) return null;

    return (
        <AlertDialog open={true} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Message Limit Reached</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-4">
                        <p>{error.message}</p>
                        <div className="text-sm text-primary-text/70">
                            <p>You can:</p>
                            <ul className="list-disc list-inside mt-2 space-y-1">
                                {error.suggestions.map((suggestion, index) => (
                                    <li key={index}>{suggestion}</li>
                                ))}
                            </ul>
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="space-x-2">
                    <AlertDialogAction onClick={onNewChat} className="bg-primary-button text-black hover:bg-primary-buttonHover">
                        Create New Chat
                    </AlertDialogAction>
                    <AlertDialogAction onClick={onClose} className="bg-primary-700/10">
                        Close
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
  };

  /**
   * Renders message content with markdown formatting and tool results.
   * Handles truncation for long user messages if necessary.
   *
   * @param {object} message - The message object to render.
   * @returns {JSX.Element} The formatted message content.
   */
  const renderMessageContentFormatted = (message) => {

    // Handles guidance prompt display if present in metadata
    const renderGuidancePrompt = () => {
      if (message.metadata && message.metadata.guidance_prompt) {
        // Extracts the name property from the guidance_prompt object
        const promptName = typeof message.metadata.guidance_prompt === 'object' 
          ? message.metadata.guidance_prompt.name 
          : message.metadata.guidance_prompt;
      
        return (
          <div className="mt-2 pt-2 border-t border-primary-border/30">
            <div className="flex items-center bg-primary-700/20 rounded-lg px-3 py-1 text-xs text-primary-text/80">
              <span className="font-medium text-primary-button mr-1">Guidance:</span> 
                {/* Sanitizes prompt name */}
                {DOMPurify.sanitize(promptName)}
            </div>
          </div>
        );
      }
      return null;
    };

    if (message.type === 'user') {
      // If the user message is long, handle expandable/collapsible logic.
      if (message.content.length > MAX_USER_MESSAGE_TRUNCATED_SIZE_CHARACTERS) {
        if (!expandedMessages.has(message.id)) {

          // Get first N lines of text when collapsed
          const lines = message.content.split('\n').slice(0, MAX_USER_MESSAGE_LINES);
          const previewText = lines.join('\n') + (lines.length < message.content.split('\n').length ? '...' : '');

          return (
            <>
              <div className="prose prose-invert max-w-none">

                {/* TODO : consider reinstating the ability to (also?) truncate based on number of characters */}
                {/* <ReactMarkdown>{message.content.slice(0, MAX_USER_MESSAGE_TRUNCATED_SIZE_CHARACTERS)}...</ReactMarkdown> */}

                {/* Uses sanitized content with safe config */}
                <ReactMarkdown {...safeMarkdownConfig}>
                  {sanitizeContent(previewText)}
                </ReactMarkdown>

              </div>
              <button
                onClick={() => handleToggleMessage(message.id)}
                className="ml-2 text-sm text-primary-button hover:bg-primary-700/20 px-2 py-1 rounded transition-colors"
              >
                Show full message
              </button>
              {renderGuidancePrompt()} {/* Renders guidance prompt below the show more button */}
            </>
          );
        } else {
          return (
            <>
              <div className="prose prose-invert max-w-none">
                {/* Uses sanitized content with safe config */}
                <ReactMarkdown {...safeMarkdownConfig}>
                  {sanitizeContent(message.content)}
                </ReactMarkdown>
              </div>
              <button
                onClick={() => handleToggleMessage(message.id)}
                className="ml-2 text-sm text-primary-button hover:bg-primary-700/20 px-2 py-1 rounded transition-colors"
              >
                Show less
              </button>
              {renderGuidancePrompt()} {/* Renders guidance prompt if present */}
            </>
          );
        }
      }
      // Render short user messages as markdown.
      return (
        <>
          <div className="prose prose-invert max-w-none">
            {/* Uses sanitized content with safe config */}
            <ReactMarkdown {...safeMarkdownConfig}>
              {sanitizeContent(message.content)}
            </ReactMarkdown>
          </div>
          {renderGuidancePrompt()} {/* Renders guidance prompt for short messages */}
        </>
      );
    } else {
      // Render assistant messages with tool results
      return (
        <>
          <div className="prose prose-invert max-w-none">
            {/* Uses sanitized content with safe config */}
            <ReactMarkdown {...safeMarkdownConfig}>
              {sanitizeContent(message.content)}
            </ReactMarkdown>
          </div>

          {renderGuidancePrompt()} {/* Renders guidance prompt if present */}

          {message.toolResults && message.toolResults.length > 0 && (
            <div className="tool-results mt-4 space-y-4">
              {message.toolResults.map((toolResult, index) => (
                <ToolResultDisplay
                  key={`tool-result-${index}`}
                  toolResult={toolResult}

                  // Include dataset UUID and message id of the dataset when attaching
                  onAttach={(selectedTypes) => {
                    const attachmentData = {};
                    Object.entries(selectedTypes).forEach(([type, isSelected]) => {
                      // Always include the type in attachmentData with correct toggle state
                      if (isSelected) {
                        // Add new attachment
                        // Each tool result should have its matching type of data
                        // Handle markdown properly by checking both dataset and filepath
                        const hasData = type === 'markdown' ? 
                          (toolResult['md_dataset'] || toolResult['md_filepath']) :
                          (toolResult[`${type}_dataset`] || toolResult[`${type}_filepath`]);

                        if (hasData) {
                          // Handle dataset UUID prefix format properly
                          const datasetUuid = type === 'markdown' ? 
                            toolResult['dataset_uuid__md'] || toolResult.dataset_uuid :
                            toolResult[`dataset_uuid__${type}`] || toolResult.dataset_uuid;

                          attachmentData[type] = {
                            isAttached: true,
                            dataset_uuid: datasetUuid
                          };
                        }
                      } else {
                        // Explicitly track detachment
                        attachmentData[type] = {
                          isAttached: false,
                          dataset_uuid: null
                        };
                      }
                    });
                    handleAttachDataset(message.id, attachmentData);
                  }}
                  attachedTypes={messageAttachments[message.id] || {}}                         // handles attachments
                />
              ))}
            </div>
          )}

        </>
      );
    }
  };

  const handleDownloadTranscript = async () => {
    if (!threadId || messages.length === 0) return;

    try {
      const response = await fetch('/api/chat/download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ threadId })
      });

      if (!response.ok) {
        throw new Error('Failed to download transcript');
      }

      // Get the content and create a blob
      const content = await response.text();
      const blob = new Blob([content], { type: 'text/markdown' });

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'chat_transcript.md';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      console.error('Error downloading transcript:', err);
      setError('Failed to download transcript. Please try again.');
    }
  };
  // Handles expandible/collapsible user message truncation
  const handleToggleMessage = useCallback((messageId) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  /**
   * Handles the deletion of a message within a chat thread.
   * 
   * This function sends a POST request to the server to delete a message by its ID and thread ID. 
   * It manages the state of the UI during the delete process, including showing a loading state 
   * and updating the local message list upon successful deletion.
   *
   * Args:
   *    message (object): The message object to delete, expected to have an `id` property.
   *
   * Behavior:
   *    - If `threadId` or `message.id` is missing, or if a deletion is already in progress (`isDeletingMessage`), 
   *      the function exits early.
   *    - A POST request is sent to the server endpoint `/api/chat/message/delete` with the message and thread IDs.
   *    - On a successful server response:
   *         - Removes the message from the local `messages` state.
   *         - Sets `setError` with a failure reason if deletion fails.
   *    - On errors, logs the error to the console and displays a user-friendly error message.
   *
   * State Updates:
   *    - `setIsDeletingMessage`: Indicates whether the delete process is in progress.
   *    - `setMessages`: Updates the list of messages after a successful deletion.
   *    - `setError`: Updates error state for display when the operation fails.
   *    - `setMessageToDelete`: Resets the currently selected message for deletion.
   *
   * Example Usage:
   *    handleDeleteMessage({ id: 'message_123' });
   *
   * Notes:
   *    - Assumes `csrfToken` is available in scope for CSRF protection.
   *    - `setMessages`, `setError`, `setIsDeletingMessage`, and `setMessageToDelete` are state setters.
   */
  const handleDeleteMessage = async (message) => {
    if (!threadId || !message.id || isDeletingMessage) return;

    // Check if this is the last message in the thread
    const isLastMessage = messages.length === 1;
    const originalContent = message.content; // Store for matching after refresh

    // Checks if message has a valid OpenAI ID format (starting with "msg_")
    const hasValidMessageId = typeof message.id === 'string' && message.id.startsWith('msg_');

    setIsDeletingMessage(true);
    try {

      // If message ID is invalid, refresh thread history first
      if (!hasValidMessageId) {
        console.log('Invalid message ID format detected, refreshing thread first...');

        // Refresh the thread to get proper message IDs
        const processedMessages = await fetchAndProcessChatHistory(threadId);
        setMessages(processedMessages);

        // Find matching message in refreshed thread
        const matchingMessage = processedMessages.find(m => 
          m.type === message.type && 
          m.content === originalContent
        );

        if (matchingMessage) {
          // Retry deletion with valid message ID
          setTimeout(() => {
            handleDeleteMessage({
              ...matchingMessage,
              id: matchingMessage.id
            });
          }, 500);

          setIsDeletingMessage(false);
          return;
        } else {
          throw new Error('Message not found after refresh');
        }
      }

      // Continue with deletion for valid message IDs
      const response = await fetch('/api/chat/message/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          threadId,
          messageId: message.id,
          isLastMessage  // Pass to backend to handle thread delete + recreate steps if this is the last message
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete message');
      }

      const data = await response.json();

      // Handle "Message not found" error specifically
      if (!data.success && data.reason && data.reason.includes('Message not found')) {
        // Refresh thread and retry deletion
        console.log('Message not found, refreshing thread and retrying...');

        // Refresh the thread
        const processedMessages = await fetchAndProcessChatHistory(threadId);
        setMessages(processedMessages);

        // Find matching message in refreshed thread
        const matchingMessage = processedMessages.find(m =>
          m.type === message.type &&
          m.content === originalContent
        );

        // If found, retry deletion with new message ID
        if (matchingMessage) {
          // Small delay to ensure thread is fully loaded
          setTimeout(() => {
            handleDeleteMessage({
              ...matchingMessage,
              id: matchingMessage.id
            });
          }, 500);
          return;
        }
      } else if (data.success) {
        if (isLastMessage) {
          // Handle last message case by creating new thread
          await handleNewChat();
          // Update sidebar
          window.dispatchEvent(new CustomEvent('chatCreated'));
        } else {
          // Remove message from local state
          setMessages(prev => prev.filter(m => m.id !== message.id));
        }
      } else {
        setError(data.reason || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message. Please try again.');
    } finally {
      setIsDeletingMessage(false);
      setMessageToDelete(null);
    }
  };

  /**
   * Helper function to process messages returned from the API into a consistent format.
   * 
   * Processes raw messages from API response into a standardized format.
   * 
   * Arguments:
   *    rawMessages (Array): The array of message objects from the API.
   * 
   * Returns:
   *    Array: Processed array of message objects with uniform structure.
   */
  const processMessagesFromData = (rawMessages) => {
    return rawMessages.map(msg => ({
      id: msg.id || Date.now() + Math.random(),
      type: msg.role, 
      content: msg.content[0]?.text?.value || '',
      timestamp: new Date(msg.created_at * 1000).toISOString(),
      toolResults: msg.toolResults || [],
      metadata: msg.metadata || {}, // Includes metadata in processed messages
    }));
  };

  /**
   * Helper function to fetch and process chat history for a given threadId.
   * 
   * Fetches and processes chat history for a given thread.
   * 
   * Arguments:
   *    threadId (Number|String): The ID of the thread to load history for.
   * 
   * Returns:
   *    Array: Processed messages array.
   * 
   * Throws:
   *    Error if the fetch operation fails.
   */
  const fetchAndProcessChatHistory = async (threadId, afterMessageId = null) => {
    const response = await fetch('/api/chat/history', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include',
      body: JSON.stringify({ threadId, after: afterMessageId })
    });

    if (!response.ok) {
      throw new Error('Failed to load chat history');
    }

    const data = await response.json();
    const processedMessages = processMessagesFromData(data.messages);
    setHasMoreMessages(data.has_more);
    return processedMessages;
  };


  // Handles activity status updates
  useEffect(() => {
    /*
    (1) Uses a Queue to store status messages.
    (2) Creates a Server-Sent Events stream to send updates to the client.
    (3) Properly handles different types of status messages.
    (4) Includes timestamps for each status update.
    (5) Uses proper typing and error handling.
    (6) Doesn't require making HTTP requests within the Flask app.

    Use send_activity_status() and the status updates will be properly streamed to the frontend.
    */

    // Create EventSource for status updates
    console.log('[DEBUG ACTIVITY STATUS] Setting up EventSource connection'); // TODO : rm logging
    const eventSource = new EventSource('/api/chat/activity-stream');

     // TODO : rm logging
    eventSource.onopen = () => {
      console.log('[DEBUG ACTIVITY STATUS] EventSource connection opened'); // TODO : rm logging
    };
     // TODO : rm logging
     eventSource.onerror = (error) => {
      console.error('[DEBUG ACTIVITY STATUS] EventSource error:', error); // TODO : rm logging
    };


    eventSource.onmessage = (event) => {
      console.log('[DEBUG ACTIVITY STATUS] Received SSE message:', event.data); // TODO : rm logging
      const data = JSON.parse(event.data);
      if (data.type === 'streaming') {
        setStreamingStatus(data.message);
      } else {
        setActivityStatus(data.message);
      }
    };

    // Cleanup on unmount
    return () => {
      console.log('[DEBUG ACTIVITY STATUS] Closing EventSource connection'); // TODO : rm logging
      eventSource.close();
    };
  }, []);


  // Gets CSRF token on component mount
  useEffect(() => {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (token) {
      setCsrfToken(token);
    }
  }, []);

  // Handler for loading more messages
  const handleLoadMore = async () => {
    if (isLoadingMore || !threadId) return;

    setIsLoadingMore(true);
    try {
      // Get the oldest message's ID
      const oldestMessage = messages[0];

      // Fetch older messages
      const processedMessages = await fetchAndProcessChatHistory(threadId, oldestMessage?.id);
  
      if (processedMessages.length > 0) {
        // Add new messages before existing ones
        setMessages(prev => [...processedMessages, ...prev]);
      }
  
    } catch (err) {
      console.error('Error loading more messages:', err);
      setError('Failed to load more messages. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Defines handleNewChat at component level so it can be used in multiple places
  const handleNewChat = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/chat/new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Checks for thread limit error
        if (errorData.error && errorData.error.includes('limited to')) {
          setThreadLimitInfo({
            maxThreadsLimit: errorData.maxThreadsLimit,
            userType: errorData.userType
          });
          setShowThreadLimitDialog(true);
          setIsLoading(false);
          return;
        }
        throw new Error('Failed to create new chat');
      }

      const data = await response.json();
      setThreadId(data.threadId);
      setMessages([]);

      // dispatch event to notify sidebar of new chat creation
      window.dispatchEvent(new CustomEvent('chatCreated'));

    } catch (err) {
      console.error('Error creating new chat:', err);
      setError('Failed to create new chat. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Loads recent thread if exists on page refresh
  useEffect(() => {
    // Checks for recent thread on component mount
    const loadRecentThread = async () => {
      try {
        const response = await fetch('/api/chat/recent', {
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken
          },
          credentials: 'include'
        });

        //  Retry using temporary_user_id from session
        if (!response.ok && response.status === 401) {
          // If unauthorized, try again with temporary user endpoint
          const tempResponse = await fetch('/api/chat/recent/temp', {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken
            },
            credentials: 'include'
          });

          if (!tempResponse.ok) {
            throw new Error('No recent thread (likely a guest user)');
          }

          const tempData = await tempResponse.json();
          if (tempData.threadId) {
            await handleThreadSwitch({ detail: { threadId: tempData.threadId } });
          }
          return; // Exit early after handling temp user
        }

        if (!response.ok) {
          throw new Error('Failed to get recent thread');
        }

        const data = await response.json();
    
        // If there's a recent thread, load it
        if (data.threadId) {
          await handleThreadSwitch({ detail: { threadId: data.threadId } });
        }
        // If no recent thread, the default behavior of starting fresh will occur

      } catch (err) {
        console.error('Starting a fresh chat session. Reason:', err);
        // Don't set this as an error, just log it
        // setError('No recent chat to load. Starting a fresh chat session.');
        // Error will trigger default new chat behavior
      }
    };

    // Load recent thread on component mount
    if (csrfToken) {
      loadRecentThread();
    }
  }, [csrfToken]);


  // Listens for thread switch events from sidebar
  const handleThreadSwitch = async (event) => { 
    const { threadId } = event.detail;
    setIsLoading(true);
    setError(null);

    try {
      const processedMessages = await fetchAndProcessChatHistory(threadId);
      setThreadId(threadId);
      setMessages(processedMessages);
    } catch (err) {
      console.error('Error switching thread:', err);
      setError('Failed to load chat history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Listens for chat thread switch event and new chat event from sidebar
  useEffect(() => {
    window.addEventListener('threadSwitch', handleThreadSwitch);
    window.addEventListener('newChat', handleNewChat);

    return () => {
      window.removeEventListener('threadSwitch', handleThreadSwitch);
      window.removeEventListener('newChat', handleNewChat);
    };
  }, [csrfToken, handleThreadSwitch]); // ensures handleThreadSwitch is included for proper cleanup

  // Loads chat history when needed
  useEffect(() => {
    const loadChatHistory = async () => {
      if (!shouldLoadHistory || !threadId || historyLoadedRef.current) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const processedMessages = await fetchAndProcessChatHistory(threadId);

        if (processedMessages.length > 0) {
          setMessages(processedMessages);
          historyLoadedRef.current = true;
        }

      } catch (err) {
        console.error('Error loading chat history:', err);
        setError('Failed to load chat history. Please refresh the page or try again later.');
      } finally {
        setIsLoading(false);
        setShouldLoadHistory(false);
      }
    };

    loadChatHistory();
  }, [threadId, csrfToken, shouldLoadHistory]);


  // Auto scrolls to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);


  // Handles dataset attachment
  const handleAttachDataset = useCallback((messageId, attachmentData) => {
    setMessageAttachments(prev => {
      const newAttachments = {...prev};
      // Handle each type's toggle state
      Object.entries(attachmentData).forEach(([type, data]) => {

        if (data.isAttached) {
            // Search through all messages for existing attachments of this type
            Object.keys(newAttachments).forEach(existingMessageId => {
              if (existingMessageId !== messageId && newAttachments[existingMessageId][type]) {
                // Remove the attachment of the same type from other messages
                delete newAttachments[existingMessageId][type];
                // Clean up empty message entries
                if (Object.keys(newAttachments[existingMessageId]).length === 0) {
                  delete newAttachments[existingMessageId];
                }
              }
            });
        }

        // Now handle the new attachment
        if (!newAttachments[messageId]) {
          newAttachments[messageId] = {};
        }
    
        if (data.isAttached === false) {
          // Remove attachment if toggled off
          delete newAttachments[messageId][type];
          if (Object.keys(newAttachments[messageId]).length === 0) {
            delete newAttachments[messageId];
          }
        } else {
          // Add or update attachment if toggled on
          newAttachments[messageId][type] = data;
        }
      });
      return newAttachments;
    });
  }, []);

  // Handles dataset detachment
  const handleDetachDataset = useCallback((messageId, type) => {
    setMessageAttachments(prev => {
      const newAttachments = {
        ...prev,
        [messageId]: {
          ...(prev[messageId] || {})
        }
      };
      delete newAttachments[messageId][type];
  
      // Removes message entry if no attachments left
      if (Object.keys(newAttachments[messageId]).length === 0) {
        delete newAttachments[messageId];
      }
      return newAttachments;
    });
  }, []);

  // Prepares attachments for new message, including message id and dataset uuid for each attachment
  const prepareMessageAttachments = useCallback(() => {
    const attachments = {};
    Object.entries(messageAttachments).forEach(([messageId, types]) => {
      Object.entries(types).forEach(([type, data]) => {
        if (data.isAttached && data.dataset_uuid) {
          attachments[type] = {
            messageId,
            type,
            dataset_uuid: data.dataset_uuid
          };
        }
      });
    });
    return attachments;
  }, [messageAttachments]);


  // Handles message submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const attachments = prepareMessageAttachments();

    // Adds guidance prompt to the message metadata if active
    const metadata = {};
    if (activeGuidance) {
        metadata.guidance_prompt = activeGuidance;
    }

    const newMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
      attachments, // includes attachments in message
      metadata,    // includes the message metadata
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setMessageAttachments({}); // clears attachments after sending
    setIsLoading(true);
    setError(null);

    // Creates AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      // Prepares the request body, including the tool use toggle state and guidance prompt if active
      const requestBody = {
        message: inputMessage,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        attachments, // Sends attachments to backend
        toolMode, // Add the tool mode preference
      };

      // Adds guidance prompt if active to be used by backend
      if (activeGuidance) {
        requestBody.guidancePrompt = {
          name: activeGuidance.name,
          llm_prompt: activeGuidance.llm_prompt
        };
      }

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,  // Includes CSRF token in headers
        },
        credentials: 'include',      // Important for CSRF
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Checks for thread limit error first
        if (errorData.error && errorData.error.includes('limited to')) {
          // Removes the pending message
          setMessages(prev => prev.slice(0, -1));
          // Sets thread limit info
          setThreadLimitInfo({
              maxThreadsLimit: errorData.maxThreadsLimit,
              userType: errorData.userType
          });
          // Shows the thread limit dialog
          setShowThreadLimitDialog(true);
          setIsLoading(false);
          return;
        }

        // Handles message limit exceeded error
        if (errorData.error === 'MESSAGE_LIMIT_EXCEEDED') {
          setMessageLimitError(errorData);
          setShowMessageLimitDialog(true);
          // Remove the pending message since it wasn't sent
          setMessages(prev => prev.slice(0, -1));
          setIsLoading(false);
          return;
        }
        throw new Error(errorData.message || 'Failed to send message because message limit exceeded');
      }

      const data = await response.json();

      // console.log('API Response data:', data);

      if (data.response && typeof data.response === 'object' && !Array.isArray(data.response)) {
        data.response = JSON.stringify(data.response, null, 2);
      }

      // Checks if we received a thread_id with history
      if (data.thread_id) {
        setThreadId(data.thread_id);
        if (data.has_history && messages.length === 0 && !historyLoadedRef.current) {
          setShouldLoadHistory(true);
        }

        // Handles thread name update from first message
        if (data.thread_name) {
          // Dispatches thread renamed event to update sidebar
          window.dispatchEvent(new CustomEvent('threadRenamed', {
            detail: { 
              threadId: data.thread_id,
              threadName: data.thread_name
            }
          }));
        }
      }

      // Handles suggestions if present
      if (data.suggestions) {
        setSuggestionOvals(data.suggestions);
      }

      if (data.response) {

        // Gets guidance prompt from either the active guidance or the response
        const responseGuidance = data.guidance_prompt || activeGuidance;
        const messageMetadata = responseGuidance ? { guidance_prompt: responseGuidance } : {};

        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'assistant',
          content: data.response,
          toolResults: data.tool_results,
          timestamp: new Date().toISOString(),
          metadata: messageMetadata, // Includes the guidance prompt metadata
        }]);
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Message generation stopped.');
      } else {
        // Checks if this is a thread limit error
        try {
          const errorData = JSON.parse(err.message);
          if (errorData.maxThreadsLimit) {
            // Removes the attempted/pending message
            setMessages(prev => prev.slice(0, -1));
            // Sets thread limit info
            setThreadLimitInfo({
              maxThreadsLimit: errorData.maxThreadsLimit,
              userType: errorData.userType
            });
            // Shows the thread limit dialog
            setShowThreadLimitDialog(true);
          } else {
            setError('[ChatInterface] Failed to send message. Please try again.');
            console.error('Error:', err);
          }
        } catch (parseError) {
          // Handles non-JSON errors
          setError('[ChatInterface] Failed to send message. Please try again.');
          console.error('Error:', err);
        }
      }
    } finally {
      setIsLoading(false);
      setIsStopping(false);
      abortControllerRef.current = null;
    }

  };

  //  Handles the Stop Generating button that appears when Assistant is thinking
  const handleStop = () => {
    if (abortControllerRef.current) {
      setIsStopping(true);
      abortControllerRef.current.abort();
    }
  };

  /**
   * Copies message content to clipboard.
   * If `navigator.clipboard` is not available, uses a fallback method.
   * 
   * Arguments:
   *    content (string): The text content to be copied.
   */  
  const copyMessage = async (content) => {
    try {
      // Checks for navigator.clipboard availability
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(content); // use Clipboard API if available
      } else {
        //  fallback method for copying text
        const textarea = document.createElement('textarea'); // create an off-screen textarea
        textarea.value = content;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy'); // use legacy execCommand as fallback
        document.body.removeChild(textarea);
      }
      // Shows temporary success message
      setError('Message copied to clipboard!');
      setTimeout(() => setError(null), 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
      setError('Failed to copy message');
    }
  };

  // Handles suggestion click with toggle
  const handleSuggestionClick = (suggestion) => {
    // If the suggestion is already the current input, clear the prompt input.
    if (inputMessage === suggestion) {
      setInputMessage('');
      inputRef.current?.focus();
      return;
    }
    setInputMessage(suggestion);
    inputRef.current?.focus();
  };

  // Handles keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl/Cmd + Enter to submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        handleSubmit(e);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleSubmit]);


  // Clears attachment from pending user input on page load/refresh
  useEffect(() => {
    setMessageAttachments({});
    setPendingAttachments({});
    // Clears any existing attachment data from sessionStorage
    sessionStorage.removeItem('messageAttachments');
  }, []);


  return (
    <div className="main-chat-ui">

      {/* Main chat interface container */}
      <div className="flex flex-col min-h-screen max-w-4xl mx-auto my-8 bg-primary-bg">

        {/* Message deletion confirmation dialog */}
        <AlertDialog 
          open={messageToDelete !== null} 
          onOpenChange={() => setMessageToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Message</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this message? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDeleteMessage(messageToDelete)}
                disabled={isDeletingMessage}
              >
                {isDeletingMessage ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Loading state for history */}
        {isLoading && shouldLoadHistory && (
          <div className="flex items-center justify-center p-4">
            <Loader className="animate-spin mr-2" />
            <span className="text-primary-text">Loading chat history...</span>
          </div>
        )}

        {/* Chat thread transcript download button */}
        <div className="flex justify-between items-center p-4 border-b border-primary-border">
          <h2 className="text-lg font-semibold text-primary-text">Chat</h2>
          <button
            onClick={handleDownloadTranscript}
            disabled={!threadId || messages.length === 0}
            className="p-2 text-primary-text hover:bg-primary-700/20 rounded-lg transition-colors disabled:opacity-50"
            title="Download chat transcript"
          >
            <Download size={20} />
          </button>
        </div>

        {/* Chat messages container */}
        <div className="flex-1 overflow-y-auto p-4 space-y-chat-gap flex flex-col justify-end">

          {/* Chat history area */}
          <div className="space-y-chat-gap">

            {/* Load More Messages button */}
            {hasMoreMessages && (
              <div className="flex justify-center mb-4">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="px-4 py-2 text-sm bg-primary-textarea text-primary-text rounded-lg hover:bg-primary-userBg disabled:opacity-50 flex items-center space-x-2"
                >
                  {isLoadingMore ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Load More Messages</span>
                  )}
                </button>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-4 rounded-lg ${
                    message.type === 'user'
                      ? 'bg-primary-userBg ml-4'
                      : 'bg-primary-textarea mr-4'
                  }`}
                >
                  {/* Timestamp */}
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm text-primary-text mr-4">
                      {new Date(message.timestamp).toLocaleDateString()} {new Date(message.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        second: '2-digit',
                        timeZoneName: 'short'  // Shows timezone abbreviation
                      })}
                    </span>
                    <div className="flex space-x-2">

                      {/* Copy assistant message button */}
                      {message.type === 'assistant' && (
                        <button
                          onClick={() => copyMessage(message.content)}
                          className="p-1 hover:bg-primary-userBg rounded"
                          title="Copy message"
                        >
                          <Copy size={16} className="text-primary-text" />
                        </button>
                      )}

                      {/* Delete message button */}
                      <button
                        onClick={() => setMessageToDelete(message)}
                        className="p-1 hover:bg-primary-userBg rounded"
                        title="Delete message"
                        disabled={isDeletingMessage}
                      >
                      <Trash2 size={16} className={`text-primary-text ${isDeletingMessage ? 'opacity-50' : ''}`} />
                      </button>
                    </div>
                  </div>

                  {/* Message content section with expandible/collapsible user message truncation logic */}
                  <div className={`whitespace-pre-wrap ${message.type === 'user' ? 'text-primary-userText' : 'text-primary-text'}`}>
                    {renderMessageContentFormatted(message)}
                  </div>
                  {/* End of : Message content section with expandible/collapsible user message truncation logic */} 
 
                </div>
              </div>
            ))}

            {isLoading && (

              <div className="flex flex-col items-center space-y-2">
                {/* Activity Feedback */}
                {activityStatus && (
                  <ActivityFeedback message={activityStatus} />
                )}

                {/* Streaming Feedback */}
                {streamingStatus && (
                  <ActivityFeedback message={streamingStatus} isStreaming={true} />
                )}

                {/* Stop Generation Button */}
                <div className="flex justify-center items-center space-x-2">
                  <Loader className="animate-spin text-primary-text" />
                  {isStopping ? (
                    <span className="text-primary-text">Stopping...</span>
                  ) : (
                    <button
                      onClick={handleStop}
                      className="text-sm text-primary-text hover:text-primary-button"
                    >
                      Stop generating
                    </button>
                  )}
                </div>
              </div>
            )}

            {error && (
              <Alert variant={error.includes('copied') ? 'info' : 'error'}>
                <AlertTitle className="text-primary-text">
                  {error.includes('copied') ? 'Success' : 'Error'}
                </AlertTitle>
                <AlertDescription className="text-primary-text">{error}</AlertDescription>
              </Alert>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Prompt input container */}

        <form onSubmit={handleSubmit} className="border-primary-border pl-2 pr-2 mb-0 pb-2">

          {/* Displays current attachments above input */}
          {Object.keys(messageAttachments).length > 0 && (
            <div className="mb-2">
              <AttachmentIndicator
                attachedTypes={Object.values(messageAttachments).reduce((acc, curr) => ({
                  ...acc,
                  ...curr
                }), {})}
                onRemove={(type) => {
                  // Removes attachment by type
                  setMessageAttachments(prev => {
                    const newAttachments = {...prev};
                    Object.keys(newAttachments).forEach(messageId => {
                      delete newAttachments[messageId][type];
                      if (Object.keys(newAttachments[messageId]).length === 0) {
                        delete newAttachments[messageId];
                      }
                    });
                    return newAttachments;
                  });
                }}
              />
            </div>
          )}

          <div className="flex items-center space-x-2">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 p-2 bg-primary-textarea text-primary-text rounded-lg border border-primary-border focus:outline-none focus:border-primary-button"
              disabled={isLoading}
              rows={3}
            />
            <button
              type="submit"
              disabled={isLoading || !inputMessage.trim()}
              className="p-2 bg-primary-button text-black rounded-lg hover:bg-primary-buttonHover disabled:opacity-50"
            >
              <Send size={20} />
            </button>
          </div>
        </form>



        {/* Intent Icons & AI Guidance Prompts*/}
        <div className="px-4">
          {/* Intent icons & suggestion ovals */}
          <IntentIcons onSelect={(intent) => {
            // If the first suggestion is already in the input, clear the prompt input and suggestion ovals.
            if (intent.suggestions && intent.suggestions.length > 0) {
              if (inputMessage === intent.suggestions[0]) {  // clear on second press
                setInputMessage('');
                setSuggestionOvals([]);
              } else {
                // Set the first suggestion as the input text
                setInputMessage(intent.suggestions[0]);
                // Set remaining suggestions as clickable ovals
                setSuggestionOvals(intent.suggestions.slice(1));
              }
              // Focus the input field
              inputRef.current?.focus();
            }
          }} />
          {/* AI Guidance Prompts - Wrapped button and guidance in flex container */}
          <div className="flex items-center mt-2"> {/* Flex container to place elements side by side */}
            <GuidancePromptsButton 
              onClick={() => setShowGuidancePanel(true)}
              isActive={activeGuidance !== null}
            />

            {/* Shows the tools toggle button */}
            <div className="ml-2">
              <ToolsToggleButton 
                toolMode={toolMode}
                onToggle={handleToolModeToggle}
              />
            </div>

            {/* Shows active guidance prompt selection inline with the button */}
            {activeGuidance && (
              <div className="ml-2 flex items-center bg-primary-700/20 rounded-lg px-3 py-1 text-xs text-primary-text/80">
                <button 
                  onClick={() => setActiveGuidance(null)}
                  className="text-primary-text/60 hover:text-primary-button mr-2"
                >
                  &times;
                </button>
                <span className="font-medium text-primary-button">Active Guidance:</span> {activeGuidance.name}
              </div>
            )}
          </div>
        </div>
        {/* Expanded suggestion ovals appear below the intent icons */}
        {suggestionOvals.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 py-2">
            {suggestionOvals.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="px-4 py-2 bg-primary-textarea text-primary-text rounded-full hover:bg-primary-userBg text-sm" // text-sm matches intent icon text size
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {/* Handles anonymous user message limit dialog */}
        {showMessageLimitDialog && (
            <MessageLimitDialog
                error={messageLimitError}
                onClose={() => setShowMessageLimitDialog(false)}
                onNewChat={() => {
                    setShowMessageLimitDialog(false);
                    handleNewChat();
                }}
            />
        )}

        {/* User thread Limit dialog */}
        <ThreadLimitDialog
          isOpen={showThreadLimitDialog}
          onClose={() => setShowThreadLimitDialog(false)}
          maxThreadsLimit={threadLimitInfo.maxThreadsLimit}
          userType={threadLimitInfo.userType}
        />
      </div>

      {/* Display panel for AI Guidance Prompts */}
      <GuidancePromptsPanel
        isOpen={showGuidancePanel}
        onClose={() => setShowGuidancePanel(false)}
        onSelectPrompt={handleSelectGuidance}
      />

    </div>
  );

};

export default ChatInterface;
